/**
 * Motor renewal API service.
 *
 * Replaces agentRenewalMock.js with real quotation-api and payment-api calls.
 * AgentRenewals.vue pages the GIS agent list itself; reminders use fetchAllAgentRenewals.
 */

import axios from 'axios';
import {
  MOTOR_COVER_COMPREHENSIVE,
  MOTOR_COVER_TPO,
  MOTOR_PROD_ID,
} from '@/constants/motorPrivate';

const PAYMENT_BASE = '/customer/payment/client-api/v1/payments/mpesa';

function paymentMpesaPath(channel, resource) {
  const agentSuffix = channel === 'agent' ? '/agent' : '';
  return `${PAYMENT_BASE}/${resource}${agentSuffix}`;
}

function asObject(data) {
  if (typeof data !== 'string') return data;
  const trimmed = data.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return data;
  try {
    return JSON.parse(trimmed);
  } catch {
    return data;
  }
}

function fail(error, fallback) {
  const body = asObject(error?.response?.data);
  const msg = body?.message
    || body?.msg
    || body?.desc
    || error?.message
    || fallback;
  return { success: false, message: msg };
}

function unwrap(response) {
  const body = asObject(response?.data);
  if (body && typeof body.success === 'boolean') {
    return body.success
      ? { success: true, data: body.data ?? body.object ?? body }
      : { success: false, message: body.message || body.msg || body.desc || 'Request failed' };
  }
  return { success: true, data: body?.data ?? body?.object ?? body };
}

function normalizeMpesaPhone(raw) {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.startsWith('0') && digits.length === 10) return `254${digits.slice(1)}`;
  if (digits.startsWith('254')) return digits;
  if (digits.startsWith('7') && digits.length === 9) return `254${digits}`;
  return digits;
}

/** quotations-api RenewalDTO uses Jackson @JsonFormat(pattern = "dd/MM/yyyy"). */
export function toRenewalDate(value) {
  if (!value) return '';
  const raw = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) return raw;
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const dd = String(parsed.getDate()).padStart(2, '0');
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${parsed.getFullYear()}`;
}

export function mapRenewalCoverTypeId(renewal) {
  const explicit = Number(renewal?.coverTypeID || renewal?.coverCode || renewal?.covertypeId);
  if (explicit === MOTOR_COVER_COMPREHENSIVE || explicit === MOTOR_COVER_TPO) return explicit;
  const label = String(renewal?.risks?.[0]?.coverType || renewal?.product || '').toUpperCase();
  if (label.includes('THIRD') || label.includes('TPO') || label === 'TP') return MOTOR_COVER_TPO;
  return MOTOR_COVER_COMPREHENSIVE;
}

export function buildRenewalPolicyDto({
  renewal,
  client = {},
  agentId,
  origin = 'P',
  additionalBenefits = [],
  valuation = null,
} = {}) {
  const batchNo = Number(renewal?.effectiveBatchNo || renewal?.batchNo);
  const dto = {
    batchNo: Number.isFinite(batchNo) ? batchNo : renewal?.batchNo,
    policyNo: renewal?.policyNo,
    prodId: Number(renewal?.prodId || renewal?.productCode) || MOTOR_PROD_ID,
    coverTypeID: mapRenewalCoverTypeId(renewal),
    renewalDate: toRenewalDate(renewal?.renewalDate),
    origin,
    fullName: (client.fullName || renewal?.clientName || renewal?.clntName || '').trim(),
    emailId: client.email || client.emailId || renewal?.prpEmail || renewal?.email || '',
    idNumber: client.idNumber || client.id || renewal?.idNumber || renewal?.clntIdNo || '',
    kraPinNumber: client.pinNo || client.pinNumber || client.kraPinNumber
      || renewal?.clntPin || renewal?.kraPinNumber || '',
    mobileNo: client.mobileNo || client.mobileNumber || renewal?.prpTel || renewal?.mobileNo || '',
    postalAddress: client.postalAddress || client.addressLine1 || renewal?.postalAddress || '',
    physicalAddress: client.physicalAddress || client.postalCode || client.addressLine2
      || renewal?.physicalAddress || '',
    agentId: agentId || client.agentId || '0',
  };
  if (Array.isArray(additionalBenefits) && additionalBenefits.length) {
    dto.additionalBenefits = additionalBenefits;
  }
  if (valuation?.valuer && valuation?.valuationDate) {
    dto.valuer = valuation.valuer;
    dto.valuationLocation = valuation.valuationLocation || '';
    dto.valuationDate = toRenewalDate(valuation.valuationDate);
  }
  return dto;
}

export async function fetchGisRenewalClient(batchNo) {
  if (!batchNo) return { success: false, message: 'Batch number is required' };
  try {
    const res = await axios.get('/customer/gis/clients/renewalClients', {
      params: { batchNo },
      _skipErrorRedirect: true,
      validateStatus: () => true,
    });
    if (res.status !== 200) {
      return { success: false, message: res.data?.desc || res.data?.message || 'Could not load client' };
    }
    return { success: true, data: res.data?.object ?? res.data?.data ?? res.data };
  } catch (e) {
    return fail(e, 'Could not load GIS client');
  }
}

/**
 * Fresh GIS batch detail for confirm screen (premium, risks, proMotoVerfy).
 * GET /customer/gis/agent/renewals/{BatchNo}
 */
export async function fetchGisRenewalBatchDetail(batchNo) {
  if (!batchNo) return { success: false, message: 'Batch number is required' };
  try {
    const res = await axios.get(
      `/customer/gis/agent/renewals/${encodeURIComponent(batchNo)}`,
      { _skipErrorRedirect: true, validateStatus: () => true },
    );
    if (res.status !== 200) {
      return {
        success: false,
        message: res.data?.desc || res.data?.message || 'Could not load renewal details',
      };
    }
    return { success: true, data: res.data?.object ?? res.data?.data ?? res.data };
  } catch (e) {
    return fail(e, 'Could not load renewal batch detail');
  }
}

// ── Renewal batch lifecycle ──

export async function upsertRenewalClient(dto) {
  try {
    const payload = {
      ...dto,
      batchNo: Number(dto?.batchNo) || dto?.batchNo,
      renewalDate: toRenewalDate(dto?.renewalDate),
    };
    const res = await axios.post(
      '/customer/quotation/renewal/policy',
      payload,
      { _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not create renewal batch');
  }
}

export async function saveRenewalCar(batchId, cars) {
  try {
    const res = await axios.post(
      `/customer/quotation/renewal/saveCar/${batchId}`,
      { cars: Array.isArray(cars) ? cars : [cars] },
      { _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not save vehicle details');
  }
}

export async function changePlan(dto) {
  try {
    const res = await axios.post(
      '/customer/quotation/renewal/changeplan',
      dto,
      { _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not change plan');
  }
}

export async function buyPlan(dto) {
  try {
    const payload = {
      batchId: Number(dto?.batchId),
      riskId: Number(dto?.riskId) || 0,
    };
    if (dto?.paymentType) {
      payload.paymentType = String(dto.paymentType).trim().toUpperCase();
    }
    const res = await axios.post(
      '/customer/quotation/renewal/buyplan',
      payload,
      { _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not select plan');
  }
}

export async function updateRenewalPremium(dto) {
  try {
    const res = await axios.post(
      '/customer/quotation/renewal/update',
      dto,
      { _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not update premium');
  }
}

export async function getRenewalProgress(batchId) {
  try {
    const res = await axios.get(
      '/customer/quotation/renewal/progress',
      { params: { batchId }, _skipErrorRedirect: true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not fetch renewal progress');
  }
}

// ── GIS renewal lookups ──

const RENEWALS_FETCH_ERROR = 'Could not fetch renewals';

export async function fetchRenewalsByMobile(mobile, covtCode) {
  try {
    const res = await axios.get(
      `/customer/gis/policies/renewals/bymobilenumber/${encodeURIComponent(mobile)}/bycovertype/${encodeURIComponent(covtCode)}`,
      { _skipErrorRedirect: true, validateStatus: () => true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, RENEWALS_FETCH_ERROR);
  }
}

export async function fetchRenewalsByIdNumber(idNumber, covtCode) {
  try {
    const res = await axios.get(
      `/customer/gis/policies/renewals/byidnumber/${encodeURIComponent(idNumber)}/bycovertype/${encodeURIComponent(covtCode)}`,
      { _skipErrorRedirect: true, validateStatus: () => true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, RENEWALS_FETCH_ERROR);
  }
}

function renewalsPage(res) {
  if (!res || res.status !== 200 || !res.data) return { items: [], total: null };
  const page = res.data?.object ?? res.data;
  const raw = page?.pageItems ?? res.data?.renewals ?? page?.renewals ?? res.data?.data;
  const total = Number(page?.totalFilteredRecords ?? res.data?.totalFilteredRecords ?? page?.totalRecords);
  return {
    items: Array.isArray(raw) ? raw : [],
    total: Number.isFinite(total) && total >= 0 ? total : null,
  };
}

/** Every page of a GIS renewals listing (API caps limit at 200). */
async function fetchAllRenewalPages(url, scopeParams, { type, pageSize, maxPages }) {
  const request = (pageNumber) => axios.get(url, {
    params: {
      ...scopeParams, type, pageNumber, limit: pageSize,
    },
    _skipErrorRedirect: true,
    validateStatus: () => true,
  });
  try {
    const firstRes = await request(1);
    if (firstRes.status !== 200 || !firstRes.data) {
      return { success: false, data: [], message: RENEWALS_FETCH_ERROR };
    }
    const first = renewalsPage(firstRes);
    const items = [...first.items];
    const known = first.total != null ? first.total : items.length;
    const remaining = Math.min(maxPages, Math.max(0, Math.ceil(known / pageSize) - 1));
    if (remaining > 0) {
      const rest = await Promise.all(
        Array.from({ length: remaining }, (_, i) => request(i + 2)),
      );
      rest.forEach((res) => items.push(...renewalsPage(res).items));
    }
    return { success: true, data: items };
  } catch (e) {
    return { ...fail(e, RENEWALS_FETCH_ERROR), data: [] };
  }
}

export async function fetchAllAgentRenewals({
  agnCode,
  email,
  type = 'pending',
  pageSize = 200,
  maxPages = 50,
}) {
  if (!agnCode && !email) return { success: false, data: [], message: 'No agent code' };
  const scope = agnCode ? { agn_code: agnCode, agnCode } : { email };
  return fetchAllRenewalPages('/customer/gis/agent/renewals', scope, { type, pageSize, maxPages });
}

/**
 * Renewals on the policies a client holds, same shape as the agent listing
 * (gis-apis GET /gis/clients/{clientCode}/renewals).
 */
export async function fetchAllClientRenewals({
  clientCode,
  type = 'pending',
  pageSize = 200,
  maxPages = 10,
}) {
  if (!clientCode) return { success: false, data: [], message: 'No client code' };
  return fetchAllRenewalPages(
    `/customer/gis/clients/${encodeURIComponent(clientCode)}/renewals`,
    {},
    { type, pageSize, maxPages },
  );
}

export async function fetchRenewalSubclassById(idNumber) {
  try {
    const res = await axios.get(
      `/customer/gis/policies/renewals/byidnumber/${encodeURIComponent(idNumber)}`,
      { _skipErrorRedirect: true, validateStatus: () => true },
    );
    return unwrap(res);
  } catch (e) {
    return fail(e, 'Could not fetch renewal subclasses');
  }
}

// ── Payment ──

export async function requestRenewalStkPush({
  batchId,
  payableAmount,
  customerPhoneNumber,
  policyNo,
  transactionReference,
  agentId,
  channel = 'customer',
}) {
  const phone = normalizeMpesaPhone(customerPhoneNumber);
  if (phone.length < 12) {
    return { success: false, message: 'Enter a valid M-Pesa phone number' };
  }
  const ref = String(transactionReference || policyNo || batchId || '')
    .replace(/\s+/g, '')
    .replace(/\//g, '')
    .slice(0, 12);
  if (!ref) {
    return { success: false, message: 'Payment reference is required' };
  }
  const payload = {
    serviceType: 'PORTAL',
    settlementProduct: 'MOTOR_RENEWAL',
    payableAmount: Math.max(1, Math.round(Number(payableAmount) || 0)),
    customerPhoneNumber: phone,
    transactionReference: ref,
    // M-Pesa caps this at 13 chars and quotations-api reads the batch id after the last '-',
    // so the short prefix keeps up to 9 batch digits intact.
    transactionDescription: `ren-${batchId}`.slice(0, 13),
  };
  if (channel === 'agent') {
    payload.transactionType = 'PAYMENT';
    payload.agentId = agentId != null ? Number(agentId) : 0;
  }
  try {
    const res = await axios.post(
      paymentMpesaPath(channel, 'stk/request'),
      payload,
      { _skipErrorRedirect: true },
    );
    const body = res?.data;
    if (body?.success && body?.data?.paymentId) {
      return { success: true, data: { paymentId: body.data.paymentId } };
    }
    return { success: false, message: body?.message || 'Failed to trigger M-Pesa' };
  } catch (e) {
    return fail(e, 'M-Pesa STK failed');
  }
}

export async function confirmRenewalPaybillPayment({ batchId, accountNumber }) {
  try {
    const res = await axios.post(
      '/customer/quotation/renewal/paybill-confirm',
      {
        batchId: Number(batchId),
        accountNumber: String(accountNumber || '').trim(),
      },
      { _skipErrorRedirect: true },
    );
    const body = res?.data;
    if (body?.success) {
      return {
        success: true,
        message: body.msg || body.message || 'Payment confirmed. The policy is being renewed.',
      };
    }
    return {
      success: false,
      message: body?.msg || body?.message || 'Payment not yet received. Complete Paybill and try again.',
    };
  } catch (e) {
    return fail(e, 'Paybill confirmation failed');
  }
}

export async function pollRenewalPaymentStatus(paymentId, channel = 'customer') {
  try {
    const res = await axios.get(
      paymentMpesaPath(channel, 'payment/query'),
      { params: { paymentId }, _skipErrorRedirect: true },
    );
    const body = res?.data;
    if (body?.success) {
      return { success: true, data: { paymentStatus: body.data?.paymentStatus } };
    }
    return { success: false, message: body?.message || 'Could not check payment' };
  } catch (e) {
    return fail(e, 'Payment status check failed');
  }
}

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40;

/**
 * Polls payment status until SUCCESSFUL, FAILED, or timeout.
 * Returns { success, paymentStatus, message }.
 */
export async function pollUntilComplete(paymentId, channel = 'customer') {
  let attempt = 0;
  while (attempt < POLL_MAX_ATTEMPTS) {
    attempt += 1;
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => { setTimeout(r, POLL_INTERVAL_MS); });
    // eslint-disable-next-line no-await-in-loop
    const result = await pollRenewalPaymentStatus(paymentId, channel);
    if (result.success) {
      const status = (result.data?.paymentStatus || '').toUpperCase();
      if (status === 'SUCCESSFUL') {
        return { success: true, paymentStatus: status };
      }
      if (status === 'FAILED' || status === 'CANCELLED') {
        return { success: false, paymentStatus: status, message: `Payment ${status.toLowerCase()}` };
      }
    }
  }
  return { success: false, paymentStatus: 'TIMEOUT', message: 'Payment confirmation timed out. Check your M-Pesa messages.' };
}
